import { Button, Input, Switch, Text, toast } from "@medusajs/ui"
import { useState } from "react"
import { Field } from "@/components/form"
import { Section } from "@/components/ui"
import { formatDate } from "@/lib/format"
import { errorMessage } from "@/lib/sdk"
import { useGoogleEvents, useSaveGoogleEvents, useTestGoogleEvents, type GoogleEventsSave, type GoogleTestResult, type HealthRow, type HealthWindow, type MissingReason } from "@/lib/google-events"
import { Badge, Banner, DefinitionList } from "@/terabizz"

/**
 * Settings → Customer events → Google server events: the Purchase from the server to GA4
 * (Measurement Protocol) and to Google Ads (click conversion upload), next to the browser's gtag.
 * Only the owner and staff with full permissions can change them; everyone with settings access
 * sees the status.
 */
export function GoogleServerEventsCard() {
  const { data, error } = useGoogleEvents()
  const save = useSaveGoogleEvents()
  const test = useTestGoogleEvents()
  const [secret, setSecret] = useState("")
  const [action, setAction] = useState<string | null>(null)
  const [result, setResult] = useState<{ target: string; r: GoogleTestResult } | null>(null)
  if (error) return <Section title="Google server events"><Text size="small" className="p-4 text-ui-fg-error">{errorMessage(error)}</Text></Section>
  if (!data) return null
  const { ga4, google_ads: ads, google_ads_tag: tag } = data.google
  const manage = data.can_manage
  const actionValue = action ?? ads.conversion_action_id ?? ""
  const secretOk = secret.trim() === "" || /^[A-Za-z0-9_-]{10,80}$/.test(secret.trim())
  const actionOk = actionValue === "" || /^\d{1,20}$/.test(actionValue)
  const set = (body: GoogleEventsSave, ok: string, after?: () => void) => save.mutate(body, { onSuccess: () => (after?.(), toast.success(ok)), onError: (e) => toast.error(errorMessage(e)) })
  const runTest = (target: "ga4" | "google_ads") => test.mutate(target, { onSuccess: (r) => (setResult({ target, r: r.test }), r.test.ok ? toast.success(r.test.message ?? "OK") : toast.error(r.test.message ?? "Check failed")), onError: (e) => toast.error(errorMessage(e)) })
  const ga4Status = !ga4.measurement_id ? ["Add the GA4 ID", "warning"] : !ga4.connected ? ["Not connected", "neutral"] : !ga4.readable ? ["Reconnect needed", "critical"] : ga4.enabled ? ["On", "success"] : ["Off", "neutral"]
  const adsStatus = !ads.ads_connected ? ["Connect Google Ads under Ad spend", "warning"] : !ads.conversion_action_id ? ["Not set up", "neutral"] : ads.enabled ? ["On", "success"] : ["Off", "neutral"]

  return (
    <Section title="Google server events">
      <div className="flex flex-col gap-3 p-4">
        <Text size="small" className="text-ui-fg-subtle">
          Sends the Purchase from the server as well as from the browser tag, so ad blockers and short-lived cookies don't lose it. Same cookie-banner rule as the pixels: with the banner on, only for visitors who accepted. Subscription renewals aren't sent unless switched on.
        </Text>
        {tag.id && tag.purchase_label && ads.active && (
          <Banner tone="warning" title="Two Google Ads Purchase conversions are set up">
            The browser tag (AW-…/{tag.purchase_label}) and the upload to conversion action {ads.conversion_action_id} both report purchases. In Google Ads → Goals → Conversions, make only one of them a Primary action, or Google Ads counts each sale twice.
          </Banner>
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-ui-border-base p-4">
        <div className="flex items-center justify-between gap-3">
          <Text size="small" weight="plus">GA4 Measurement Protocol</Text>
          <Badge tone={ga4Status[1] as any}>{ga4Status[0]}</Badge>
        </div>
        <Text size="xsmall" className="text-ui-fg-subtle">
          The purchase (transaction ID = order ID, items, value without tax) goes to {ga4.measurement_id ?? "the GA4 property under Marketing pixels"} from the server, joined to the shopper's GA4 session. GA4 keeps one purchase per transaction ID for the same visitor, so the browser's and the server's count once.
        </Text>
        {ga4.connected && (
          <DefinitionList
            rows={[
              ["API secret", ga4.secret_last4 ? `••••${ga4.secret_last4}` : "—"],
              ["Browser purchase (gtag)", ga4.browser_purchase ? "Also sent" : "Not sent: server only"],
              ["Subscription renewals", ga4.send_renewals ? "Sent" : "Not sent"],
              ...(ga4.last_test_at ? ([["Last check", `${ga4.last_test_ok ? "Accepted" : "Failed"} · ${formatDate(ga4.last_test_at)}`]] as [string, string][]) : []),
            ]}
          />
        )}
        {manage && (
          <>
            <Field label={ga4.connected ? "Replace the API secret" : "Measurement Protocol API secret"} hint={secretOk ? "GA4 → Admin → Data streams → your web stream → Measurement Protocol API secrets → Create. Stored encrypted; never shown again." : "That doesn't look like an API secret."}>
              <Input type="password" autoComplete="off" value={secret} aria-invalid={!secretOk} maxLength={80} placeholder={ga4.secret_last4 ? `••••${ga4.secret_last4}` : "API secret value"} onChange={(e) => setSecret(e.target.value)} />
            </Field>
            <label className="flex items-start gap-2 text-[13px]">
              <Switch checked={ga4.browser_purchase} disabled={save.isPending || !ga4.connected} onCheckedChange={(v) => set({ ga4: { browser_purchase: v } }, v ? "The browser also sends GA4's purchase" : "GA4's purchase now comes from the server only")} />
              <span>
                Also send the purchase from the browser
                <Text size="xsmall" className="text-ui-fg-subtle">On: both, GA4 deduplicates by transaction ID. Off: the server's only (the thank-you page leaves GA4's purchase out).</Text>
              </span>
            </label>
            <label className="flex items-start gap-2 text-[13px]">
              <Switch checked={ga4.send_renewals} disabled={save.isPending || !ga4.connected} onCheckedChange={(v) => set({ ga4: { send_renewals: v } }, v ? "Renewals are sent to GA4" : "Renewals aren't sent to GA4")} />
              <span>
                Send subscription renewals as purchases
                <Text size="xsmall" className="text-ui-fg-subtle">Off by default: renewals aren't a website visit.</Text>
              </span>
            </label>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-[13px]">
                <Switch checked={ga4.enabled} disabled={save.isPending || !ga4.connected} onCheckedChange={(v) => set({ ga4: { enabled: v } }, v ? "GA4 server purchases on" : "GA4 server purchases off")} />
                Send server purchases
              </label>
              <div className="flex flex-wrap gap-2">
                <Button size="small" disabled={!secretOk || secret.trim() === ""} isLoading={save.isPending} onClick={() => set({ ga4: { api_secret: secret.trim() } }, "API secret saved", () => setSecret(""))}>Save</Button>
                <Button size="small" variant="secondary" disabled={!ga4.active} isLoading={test.isPending && test.variables === "ga4"} onClick={() => runTest("ga4")}>Send test event</Button>
                {ga4.connected && <Button size="small" variant="danger" isLoading={save.isPending} onClick={() => set({ ga4: { remove_secret: true } }, "API secret removed")}>Remove secret</Button>}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-ui-border-base p-4">
        <div className="flex items-center justify-between gap-3">
          <Text size="small" weight="plus">Google Ads conversion upload</Text>
          <Badge tone={adsStatus[1] as any}>{adsStatus[0]}</Badge>
        </div>
        <Text size="xsmall" className="text-ui-fg-subtle">
          For orders whose last paid click was a Google ad (gclid, gbraid or wbraid), the purchase is uploaded to a conversion action created as Import → clicks, with the order ID (Google counts it once) and, for gclid clicks, the customer's SHA-256 hashed email, phone, name and address (enhanced conversions). Uses the Google Ads connection under Ad spend{ads.customer_id ? ` (account ${ads.customer_id})` : ""}.
        </Text>
        {ads.conversion_action_id && (
          <DefinitionList
            rows={[
              ["Conversion action", ads.conversion_action_id],
              ["Subscription renewals", ads.send_renewals ? "Sent" : "Not sent"],
              ...(ads.last_test_at ? ([["Last check", `${ads.last_test_ok ? "OK" : "Failed"} · ${formatDate(ads.last_test_at)}`]] as [string, string][]) : []),
            ]}
          />
        )}
        {manage && (
          <>
            <Field label="Conversion action ID" hint={actionOk ? "Google Ads → Goals → Conversions → the Import → clicks action: the number after ctId= in the address bar. Leave empty to turn the upload off." : "Digits only."}>
              <Input value={actionValue} aria-invalid={!actionOk} maxLength={20} placeholder="123456789" onChange={(e) => setAction(e.target.value.trim())} />
            </Field>
            <label className="flex items-start gap-2 text-[13px]">
              <Switch checked={ads.send_renewals} disabled={save.isPending || !ads.conversion_action_id} onCheckedChange={(v) => set({ google_ads: { send_renewals: v } }, v ? "Renewals are uploaded to Google Ads" : "Renewals aren't uploaded")} />
              <span>
                Upload subscription renewals as conversions
                <Text size="xsmall" className="text-ui-fg-subtle">Off by default: renewals aren't caused by an ad click.</Text>
              </span>
            </label>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-[13px]">
                <Switch checked={ads.enabled} disabled={save.isPending || !ads.conversion_action_id} onCheckedChange={(v) => set({ google_ads: { enabled: v } }, v ? "Google Ads upload on" : "Google Ads upload off")} />
                Upload purchases
              </label>
              <div className="flex flex-wrap gap-2">
                <Button size="small" disabled={!actionOk || actionValue === (ads.conversion_action_id ?? "")} isLoading={save.isPending} onClick={() => set({ google_ads: { conversion_action_id: actionValue } }, actionValue ? "Conversion action saved" : "Google Ads upload removed", () => setAction(null))}>Save</Button>
                <Button size="small" variant="secondary" disabled={!ads.active} isLoading={test.isPending && test.variables === "google_ads"} onClick={() => runTest("google_ads")}>Check conversion action</Button>
              </div>
            </div>
          </>
        )}
        {!manage && <Text size="xsmall" className="text-ui-fg-subtle">Only store owners and staff with full permissions can change Google server events.</Text>}
      </div>
      {result && (
        <div className="flex flex-col gap-1 border-t border-ui-border-base p-4">
          <Text size="xsmall" weight="plus">{result.target === "ga4" ? "GA4" : "Google Ads"}: {result.r.message}</Text>
          {result.r.response ? <pre className="max-h-48 overflow-auto rounded-md bg-ui-bg-subtle p-2 text-[11px] leading-4" aria-label="Google's response">{JSON.stringify(result.r.response, null, 2)}</pre> : null}
        </div>
      )}
    </Section>
  )
}

/** Tracking health: per server destination, on/off and the last 24 hours of the outbox. */
export function TrackingHealthCard() {
  const { data } = useGoogleEvents()
  if (!data) return null
  return (
    <Section title="Tracking health">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-[13px]">
          <thead>
            <tr className="text-left text-ui-fg-subtle">
              <th className="px-4 py-2 font-normal">Server destination</th>
              <th className="px-4 py-2 font-normal">Status</th>
              <th className="px-4 py-2 text-right font-normal">Sent 24h</th>
              <th className="px-4 py-2 text-right font-normal">Failed 24h</th>
              <th className="px-4 py-2 text-right font-normal">Waiting</th>
              <th className="px-4 py-2 font-normal">Last sent</th>
            </tr>
          </thead>
          <tbody>
            {data.health.map((h) => (
              <tr key={h.id} className="border-t border-ui-border-base align-top">
                <td className="px-4 py-2">
                  {h.label}
                  {h.last_failure && h.failed_24h > 0 ? <Text size="xsmall" className="text-ui-fg-error">{h.last_failure.error ?? "Failed"}</Text> : null}
                </td>
                <td className="px-4 py-2"><Badge tone={!h.active ? "neutral" : h.failed_24h > 0 ? "warning" : "success"}>{h.active ? (h.failed_24h > 0 ? "Failures" : "On") : "Off"}</Badge></td>
                <td className="px-4 py-2 text-right font-mono">{h.sent_24h}</td>
                <td className="px-4 py-2 text-right font-mono">{h.failed_24h}</td>
                <td className="px-4 py-2 text-right font-mono">{h.waiting}</td>
                <td className="px-4 py-2">{h.last_sent_at ? `${h.last_sent_event ?? ""} · ${formatDate(h.last_sent_at)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TrackingBreakdown rows={data.health} />
      <Text size="xsmall" className="px-4 pb-3 text-ui-fg-subtle">Browser tags (pixels, gtag) report straight to each platform: check them there (Meta Events Manager, Google Ads tag diagnostics, GA4 DebugView).</Text>
    </Section>
  )
}

const REASON: Record<MissingReason, string> = {
  not_queued: "not queued (cookies declined, destination off at the time, or before it was connected)",
  waiting: "waiting to send",
  failed: "failed",
  skipped: "skipped",
}

/**
 * Per destination: sent / failed / skipped per event name, and Purchases sent vs paid storefront
 * orders placed (one sent Purchase per order is right; missing and duplicate ones are listed).
 */
function TrackingBreakdown({ rows }: { rows: HealthRow[] }) {
  const [win, setWin] = useState<HealthWindow>("24h")
  const shown = rows.filter((h) => h.active || (h.by_event?.[win] ?? []).length)
  if (!shown.length) return null
  return (
    <div className="flex flex-col gap-3 border-t border-ui-border-base px-4 py-3" data-testid="tracking-breakdown">
      <div className="flex items-center justify-between gap-2">
        <Text size="small" weight="plus">By event</Text>
        <div className="flex gap-1" role="group" aria-label="Window">
          {(["24h", "7d"] as HealthWindow[]).map((w) => (
            <Button key={w} size="small" variant={w === win ? "primary" : "secondary"} onClick={() => setWin(w)} aria-pressed={w === win}>
              {w === "24h" ? "Last 24 hours" : "Last 7 days"}
            </Button>
          ))}
        </div>
      </div>
      {shown.map((h) => {
        const evs = h.by_event?.[win] ?? []
        const pc = h.purchases?.[win] ?? null
        return (
          <div key={h.id} className="flex flex-col gap-2 rounded-md border border-ui-border-base">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 pt-2 text-[13px] font-medium">
              {h.label}
              {pc && h.active ? (
                <Badge tone={pc.duplicates ? "critical" : pc.missing ? "attention" : "success"}>
                  {pc.matched} of {pc.orders_placed} orders · {pc.duplicates ? `${pc.duplicates} duplicate${pc.duplicates === 1 ? "" : "s"}` : pc.missing ? `${pc.missing} missing` : "all sent"}
                </Badge>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-[13px]">
                <thead>
                  <tr className="text-left text-ui-fg-subtle">
                    <th className="px-3 py-1 font-normal">Event</th>
                    <th className="px-3 py-1 text-right font-normal">Sent</th>
                    <th className="px-3 py-1 text-right font-normal">Failed</th>
                    <th className="px-3 py-1 text-right font-normal">Skipped</th>
                    <th className="px-3 py-1 text-right font-normal">Waiting</th>
                  </tr>
                </thead>
                <tbody>
                  {evs.length ? (
                    evs.map((e) => (
                      <tr key={e.event_name} className="border-t border-ui-border-base">
                        <td className="px-3 py-1">{e.event_name}</td>
                        <td className="px-3 py-1 text-right font-mono">{e.sent.toLocaleString()}</td>
                        <td className="px-3 py-1 text-right font-mono">{e.failed.toLocaleString()}</td>
                        <td className="px-3 py-1 text-right font-mono">{e.skipped.toLocaleString()}</td>
                        <td className="px-3 py-1 text-right font-mono">{e.waiting.toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-t border-ui-border-base"><td className="px-3 py-1 text-ui-fg-subtle" colSpan={5}>Nothing in this window.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {pc && h.active ? (
              <div className="flex flex-col gap-1 px-3 pb-2 text-[12px] text-ui-fg-subtle" data-testid={`purchase-check-${h.id}`}>
                <span>
                  Purchases sent vs orders placed: {pc.orders_placed} paid storefront order{pc.orders_placed === 1 ? "" : "s"}, {pc.matched} with a Purchase sent
                  {pc.renewal_purchases ? `; ${pc.renewal_purchases} renewal Purchase${pc.renewal_purchases === 1 ? "" : "s"} sent too` : ""}. Renewals and draft orders aren't storefront orders.
                </span>
                {pc.missing ? (
                  <span>
                    Missing {pc.missing}:{" "}
                    {(Object.keys(REASON) as MissingReason[])
                      .filter((r) => pc.missing_by_reason[r])
                      .map((r) => `${pc.missing_by_reason[r]} ${REASON[r]}`)
                      .join("; ")}
                    {pc.missing_orders.length ? ` (e.g. ${pc.missing_orders.slice(0, 5).map((o) => `#${o.display_id ?? o.id}`).join(", ")})` : ""}.
                  </span>
                ) : null}
                {pc.duplicates ? (
                  <span className="text-ui-fg-error">
                    {pc.duplicates} order{pc.duplicates === 1 ? "" : "s"} with more than one Purchase sent: {pc.duplicate_orders.slice(0, 5).map((d) => `${d.id} (${d.count})`).join(", ")}. The platform may count them twice.
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
